"use client";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import Autoplay from "embla-carousel-autoplay";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../../components/ui/carousel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { useApi } from "../../hooks/useApi";
import { useIsMobile, useBreakpoint } from "../../hooks/use-mobile";
import { 
  Smartphone, 
  MapPin, 
  Activity,
  AlertCircle,
  TrendingUp,
  Car,
  Navigation,
} from "lucide-react";
import { toast } from "react-toastify";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [carouselApi, setCarouselApi] = useState(null);
  const [carouselPage, setCarouselPage] = useState(0);
  const [carouselPagesTotal, setCarouselPagesTotal] = useState(0);
  const carouselPlugin = useRef(Autoplay({ delay: 5000, stopOnInteraction: true }));
  const { get } = useApi();
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();
  const cardsPerPage = breakpoint === "mobile" ? 1 : breakpoint === "tablet" ? 2 : 3;
  const carouselItemBasis =
    breakpoint === "mobile" ? "" : breakpoint === "tablet" ? "basis-1/2" : "basis-1/3";
  const carouselSkeletonCount = cardsPerPage;

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  const fetchVehicles = useCallback(async () => {
    try {
      setVehiclesLoading(true);
      const response = await get('/customer/my-vehicles?per_page=100&page=1');
      if (response?.success && Array.isArray(response?.data)) {
        setVehicles(response.data);
      }
    } catch (err) {
    } finally {
      setVehiclesLoading(false);
    }
  }, [get]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    if (!carouselApi) return;
    const n = carouselApi.scrollSnapList().length;
    setCarouselPagesTotal(Math.ceil(n / cardsPerPage));
    setCarouselPage(Math.floor(carouselApi.selectedScrollSnap() / cardsPerPage));

    const handleSelect = () => {
      setCarouselPage(Math.floor(carouselApi.selectedScrollSnap() / cardsPerPage));
    };
    carouselApi.on("select", handleSelect);

    return () => {
      carouselApi.off("select", handleSelect);
    };
  }, [carouselApi, cardsPerPage]);

  const periodOptions = [
    { value: 'today', label: 'Today', apiParam: 'today' },
    { value: 'lastWeek', label: 'Last Week', apiParam: 'last_week' },
    { value: 'thisWeek', label: 'This Week', apiParam: 'this_week' },
    { value: 'lastMonth', label: 'Last Month', apiParam: 'last_month' },
    { value: 'thisMonth', label: 'This Month', apiParam: 'this_month' }
  ];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    fetchDashboardData();
  };

  // Map period to API date parameter
  const getDateParam = () => {
    const selectedOption = periodOptions.find(opt => opt.value === selectedPeriod);
    return selectedOption?.apiParam || 'today';
  };

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const dateParam = getDateParam();
      
      // Build query parameters
      const params = new URLSearchParams({
        filter: dateParam
      });
      
      const response = await get(`/customer/dashboard?${params.toString()}`);
      
      if (response?.success && response?.data) {
        setDashboardData(response.data);
      } else {
        toast.error('Failed to load dashboard data');
      }
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      setIsLoading(false);
    }
  };

  // Get card values from API data
  const getCardValues = () => {
    if (!dashboardData?.cards) {
      return {
        mappedVehicles: 0,
        mappedDevices: 0,
        activeVehicles: 0,
        inactiveVehicles: 0
      };
    }
    return {
      mappedVehicles: dashboardData.cards.mapped_vehicles || 0,
      mappedDevices: dashboardData.cards.mapped_devices || 0,
      activeVehicles: dashboardData.cards.active_vehicles || 0,
      inactiveVehicles: dashboardData.cards.inactive_vehicles || 0
    };
  };

  const { mappedVehicles, mappedDevices, activeVehicles, inactiveVehicles } = getCardValues();

  // Get bar chart data from API
  const getBarChartData = () => {
    if (!dashboardData?.bar_chart) {
      return {
        labels: ["Mapped Vehicles", "Mapped Devices", "Active Vehicles", "Inactive Vehicles"],
        datasets: [
          {
            label: "Count",
            data: [0, 0, 0, 0],
            backgroundColor: [
              "rgba(34, 197, 94, 0.8)",
              "rgba(99, 102, 241, 0.8)",
              "rgba(234, 179, 8, 0.8)",
              "rgba(239, 68, 68, 0.8)",
            ],
            borderColor: [
              "rgb(34, 197, 94)",
              "rgb(99, 102, 241)",
              "rgb(234, 179, 8)",
              "rgb(239, 68, 68)",
            ],
            borderWidth: 2,
          },
        ],
      };
    }

    const barChart = dashboardData.bar_chart;
    return {
      labels: ["Mapped Vehicles", "Mapped Devices", "Active Vehicles", "Inactive Vehicles"],
      datasets: [
        {
          label: "Count",
          data: [
            barChart.mapped_vehicles || 0,
            barChart.mapped_devices || 0,
            barChart.active_vehicles || 0,
            barChart.inactive_vehicles || 0,
          ],
          backgroundColor: [
            "rgba(34, 197, 94, 0.8)", // green
            "rgba(99, 102, 241, 0.8)", // indigo/blue
            "rgba(234, 179, 8, 0.8)", // yellow
            "rgba(239, 68, 68, 0.8)", // red
          ],
          borderColor: [
            "rgb(34, 197, 94)",
            "rgb(99, 102, 241)",
            "rgb(234, 179, 8)",
            "rgb(239, 68, 68)",
          ],
          borderWidth: 2,
        },
      ],
    };
  };

  // Get doughnut chart data from API
  const getDoughnutChartData = () => {
    if (!dashboardData?.doughnut_chart || dashboardData.doughnut_chart.length === 0) {
      return {
        labels: ["Mapped Vehicles", "Mapped Devices", "Active Vehicles", "Inactive Vehicles"],
        datasets: [
          {
            data: [0, 0, 0, 0],
            backgroundColor: [
              "rgba(34, 197, 94, 0.8)",
              "rgba(99, 102, 241, 0.8)",
              "rgba(234, 179, 8, 0.8)",
              "rgba(239, 68, 68, 0.8)",
            ],
            borderColor: [
              "rgb(34, 197, 94)",
              "rgb(99, 102, 241)",
              "rgb(234, 179, 8)",
              "rgb(239, 68, 68)",
            ],
            borderWidth: 2,
          },
        ],
      };
    }

    const doughnutChart = dashboardData.doughnut_chart;
    return {
      labels: doughnutChart.map(item => item.label),
      datasets: [
        {
          data: doughnutChart.map(item => item.value),
          backgroundColor: [
            "rgba(34, 197, 94, 0.8)",
            "rgba(99, 102, 241, 0.8)",
            "rgba(234, 179, 8, 0.8)",
            "rgba(239, 68, 68, 0.8)",
          ],
          borderColor: [
            "rgb(34, 197, 94)",
            "rgb(99, 102, 241)",
            "rgb(234, 179, 8)",
            "rgb(239, 68, 68)",
          ],
          borderWidth: 2,
        },
      ],
    };
  };

  const barData = getBarChartData();
  const doughnutData = getDoughnutChartData();

  const barChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: isMobile ? 8 : 12,
        titleFont: {
          size: isMobile ? 12 : 14
        },
        bodyFont: {
          size: isMobile ? 11 : 13
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          stepSize: 10,
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: isMobile ? 10 : 12
          },
          maxRotation: isMobile ? 45 : 0,
          minRotation: isMobile ? 45 : 0
        }
      },
    },
  }), [isMobile]);

  const doughnutChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: isMobile ? 8 : 15,
          usePointStyle: true,
          font: {
            size: isMobile ? 10 : 12
          },
          boxWidth: isMobile ? 10 : 12
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: isMobile ? 8 : 12,
        titleFont: {
          size: isMobile ? 12 : 14
        },
        bodyFont: {
          size: isMobile ? 11 : 13
        }
      }
    },
  }), [isMobile]);

  const summaryCards = [
    {
      title: "Mapped Vehicles",
      value: mappedVehicles,
      icon: MapPin,
      color: "bg-green-500",
      bgGradient: "from-green-500 to-green-600",
      description: "Total mapped vehicles",
    },
    {
      title: "Mapped Devices",
      value: mappedDevices,
      icon: Smartphone,
      color: "bg-blue-500",
      bgGradient: "from-blue-500 to-blue-600",
      description: "Total mapped devices",
    },
    {
      title: "Active Vehicles",
      value: activeVehicles,
      icon: Activity,
      color: "bg-yellow-500",
      bgGradient: "from-yellow-500 to-yellow-600",
      description: "Currently active vehicles",
    },
    {
      title: "Inactive Vehicles",
      value: inactiveVehicles,
      icon: AlertCircle,
      color: "bg-red-500",
      bgGradient: "from-red-500 to-red-600",
      description: "Inactive vehicles",
    },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 animate-in fade-in duration-500 bg-background text-foreground min-h-screen overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">Customer Dashboard</h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Overview of your vehicles and devices</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-full sm:w-[200px] h-10 sm:h-11 rounded-xl text-muted-foreground border-1 border-muted-foreground/20 text-sm sm:text-base">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent className="border-1 border-muted-foreground/20">
              {periodOptions.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-sm sm:text-base">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* All Vehicles Map Quick Access */}
      <Card className="border-1 border-muted-foreground/20 bg-gradient-to-r from-primary/5 via-background to-primary/10">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                <Navigation className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                All Vehicles Dashboard
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Open map view for all your vehicles in one place.
              </p>
            </div>
            <Button
              className="h-10 px-4 bg-[#6366F1] text-white hover:bg-[#4F46E5]"
              onClick={() => navigate("/customer/map")}
            >
              <MapPin className="h-4 w-4 mr-2" />
              Open All Vehicles Map
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* My Vehicles Carousel — 1 per page mobile, 2 desktop (arrows inset; avoid overflow-hidden clipping) */}
      <section className="space-y-5">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-foreground tracking-tight">My Vehicles</h2>
            <p className="text-xs text-muted-foreground mt-0.5 lg:hidden">Swipe to browse your vehicles</p>
          </div>
          {!vehiclesLoading && vehicles.length > 0 && (
            <span className="text-xs font-medium tabular-nums text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-full border-1">
              {vehicles.length} total
            </span>
          )}
        </div>

        {vehiclesLoading ? (
          <div className="flex gap-4 overflow-hidden">
            {Array.from({ length: carouselSkeletonCount }, (_, i) => i + 1).map((i) => (
              <div
                key={i}
                className={`flex-shrink-0 rounded-2xl border border-border/60 overflow-hidden bg-muted/20 shadow-sm ${
                  breakpoint === "mobile"
                    ? "w-full max-w-[320px]"
                    : breakpoint === "tablet"
                      ? "w-[calc(50%-0.5rem)] max-w-[320px]"
                      : "w-[calc(33.333%-0.67rem)] max-w-[320px]"
                }`}
              >
                <div className="h-24 bg-gradient-to-br from-muted to-muted/40 animate-pulse" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-muted rounded-lg animate-pulse w-4/5" />
                  <div className="h-8 bg-muted rounded-full animate-pulse w-2/3" />
                  <div className="h-10 bg-muted rounded-lg animate-pulse w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="border-1 border-muted-foreground/20 bg-muted/20">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Car className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm font-medium text-foreground">No vehicles yet</p>
              <p className="text-xs text-muted-foreground mt-1">Your assigned vehicles will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <Carousel
            setApi={setCarouselApi}
            opts={{ align: "start", loop: true }}
            plugins={[carouselPlugin.current]}
            className="w-full max-w-full pl-1 pr-1 sm:pl-2 sm:pr-2 border-1"
          >
            <CarouselContent className="-ml-4">
              {vehicles.map((v) => (
                <CarouselItem key={v.vehicle_id || v.device_id} className={carouselItemBasis}>
                  <Card className="group relative overflow-hidden rounded-2xl border-1 border-muted-foreground/20  bg-card shadow-md hover:shadow-xl  transition-all duration-300 h-full p-3">
                    <div className="relative h-[5.25rem] overflow-hidden border-1 border-muted-foreground/20  from-primary/20 via-primary/8 to-muted/40 dark:from-primary/25 dark:via-primary/10 dark:to-muted/30 p-3">
                      <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32  rounded-full bg-primary/25 blur-2xl" />
                      <div className="pointer-events-none absolute -left-6 bottom-0 h-24 w-24 rounded-full bg-primary/15 blur-xl" />
                      <div className="absolute inset-0 bg-gradient-to-r from-background/0 via-background/20 to-background/50 dark:to-background/40" />
                      <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background/90 shadow-sm ring-1 ring-border/80 backdrop-blur-sm dark:bg-background/80">
                            <Car className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                              Vehicle
                            </p>
                            <h3 className="font-semibold text-foreground truncate text-sm sm:text-base leading-tight">
                              {v.vehicle_name || "Unnamed vehicle"}
                            </h3>
                          </div>
                        </div>
                        <div className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background/60 ring-1 ring-border/60 backdrop-blur-sm">
                          <MapPin className="h-4 w-4 text-primary" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4 sm:p-5 flex flex-col gap-3 flex-1 pt-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-full border-1 border-muted-foreground/20 bg-muted/50 px-3 py-1 font-mono text-xs font-semibold tracking-wide text-foreground tabular-nums">
                          {v.vehicle_number || "—"}
                        </span>
                        {v.customer_assigned_at && (
                          <span className="text-[10px] text-muted-foreground sm:text-xs">
                            Since {new Date(v.customer_assigned_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-start gap-2 rounded-xl border-1 border-muted-foreground/20 bg-muted/50 px-3 py-2.5 dark:bg-muted/20">
                        <Smartphone className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Device IMEI</p>
                          <p className="font-mono text-[11px] sm:text-xs text-foreground break-all leading-snug">
                            {v.imei_number || "Not available"}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="w-full mt-auto h-10 text-sm font-semibold shadow-sm bg-[#6366F1] text-white hover:bg-[#4F46E5]"
                        disabled={!(v.device_id || v.deviceId)}
                        onClick={() => {
                          const deviceId = v.device_id || v.deviceId;
                          if (!deviceId) return;
                          navigate(`/customer/map/vehicle-half/${encodeURIComponent(deviceId)}`, {
                            state: {
                              vehicle_number: v.vehicle_number,
                              vehicle_name: v.vehicle_name,
                            },
                          });
                        }}
                      >
                        <Navigation className="h-4 w-4 mr-2" />
                        Open live map
                      </Button>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-2 sm:-left-3" />
            <CarouselNext className="-right-2 sm:-right-3" />
            {carouselPagesTotal > 1 && (
              <div className="flex justify-center gap-2 mt-3 sm:mt-5">
                {Array.from({ length: carouselPagesTotal }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to page ${i + 1}`}
                    onClick={() => carouselApi?.scrollTo(i * cardsPerPage)}
                    className={`h-2 rounded-full transition-all duration-200 ${
                      i === carouselPage ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </Carousel>
        )}
      </section>

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 sm:h-32 bg-muted/70 rounded-xl animate-pulse"></div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {summaryCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card 
                key={index} 
                className="group relative overflow-hidden border-1 border-muted-foreground/20 bg-gradient-to-br from-background to-muted/30 hover:shadow-lg transition-all duration-300 animate-in slide-in-from-bottom-4"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className={`absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${card.bgGradient} opacity-10 rounded-full blur-2xl`}></div>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 sm:mb-2 truncate">{card.title}</p>
                      <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-0.5 sm:mb-1">{card.value}</h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2">{card.description}</p>
                    </div>
                    <div className={`p-2 sm:p-2.5 rounded-xl bg-gradient-to-br ${card.bgGradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Bar Chart */}
        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300">
          <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 flex-shrink-0" />
                  <span className="truncate">Customer Overview</span>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-1">
                  Visual representation of your data
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {isLoading ? (
              <div className="h-[280px] sm:h-64 bg-muted/50 rounded-lg animate-pulse"></div>
            ) : (
              <div className="h-[280px] sm:h-64 -mx-2 sm:mx-0">
                <Bar data={barData} options={barChartOptions} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Doughnut Chart */}
        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300">
          <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                  <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 flex-shrink-0" />
                  <span className="truncate">Customer Distribution</span>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-1">
                  Distribution of vehicles and devices
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {isLoading ? (
              <div className="h-[280px] sm:h-64 bg-muted/50 rounded-lg animate-pulse"></div>
            ) : (
              <div className="h-[280px] sm:h-64 -mx-2 sm:mx-0">
                <Doughnut data={doughnutData} options={doughnutChartOptions} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerDashboard;
